import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SwapOutlined, CheckOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';

const ChatOptions = ({
  options,
  onSelect,
  isLoading,
  compareMode,
  selectedCards,
  onCardToggle,
  onCompare,
  checklistMode,
  selectedCriteria,
  onCriteriaToggle,
  onRecommend,
  wizardMode,
  wizardSteps,
  currentStepIndex,
  wizardSelections,
  onWizardToggle,
  onWizardNext,
  onWizardPrev,
  onWizardSubmit,
  isInline = false,
  isPast = false,
}) => {
  const listRef = useRef(null);

  useEffect(() => {
    if (options && options.length > 0 && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [options]);

  if (isLoading) {
    return null;
  }

  if (!options || options.length === 0) {
    return null;
  }

  const isSelectionMode = compareMode || checklistMode || wizardMode;
  const selectedItems = compareMode ? selectedCards : (checklistMode ? selectedCriteria : []);

  const isSelected = (option) =>
    selectedItems?.some((c) => c.optionType === option.optionType);

  const isDisabled = (option) =>
    compareMode && selectedCards?.length >= 2 && !isSelected(option);

  const handleClick = (option) => {
    if (compareMode && option.optionType !== 'MAIN_MENU') {
      onCardToggle?.(option);
    } else if (checklistMode && option.optionType !== 'MAIN_MENU') {
      onCriteriaToggle?.(option);
    } else {
      onSelect(option);
    }
  };

  // Wizard mode: ozel render
  if (wizardMode && wizardSteps?.length > 0) {
    const currentStep = wizardSteps[currentStepIndex];
    const stepItems = currentStep?.children || [];
    const stepSelections = wizardSelections?.[currentStep?.optionType] || [];
    const isLastStep = currentStepIndex === wizardSteps.length - 1;
    const isFirstStep = currentStepIndex === 0;
    const totalSelections = Object.values(wizardSelections || {}).reduce((sum, arr) => sum + arr.length, 0);

    // MAIN_MENU option'i bul (wizard disinda gostermek icin)
    const mainMenuOption = options.find(o => o.optionType === 'MAIN_MENU');

    return (
      <div ref={listRef} className={`chat-options wizard-active ${isInline ? 'chat-options--inline' : ''}`}>
        {/* Step indicator */}
        <div className="wizard-step-indicator">
          {wizardSteps.map((step, i) => (
            <div
              key={step.optionType}
              className={`wizard-step-dot${i === currentStepIndex ? ' active' : ''}${i < currentStepIndex ? ' completed' : ''}`}
              title={step.displayText}
            />
          ))}
          <span className="wizard-step-label">Adim {currentStepIndex + 1}/{wizardSteps.length}</span>
        </div>

        {/* Step items */}
        <div className="chat-options-list">
          {stepItems.map((item, index) => {
            const selected = stepSelections.some((s) => s.optionType === item.optionType);
            return (
              <motion.button
                key={item.optionType}
                className={`chat-option-btn${selected ? ' selected' : ''}`}
                onClick={() => onWizardToggle?.(currentStep.optionType, item)}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className={`chat-option-check${selected ? ' checked' : ''}`}>
                  {selected && <CheckOutlined />}
                </span>
                <span className="chat-option-emoji">{item.emoji}</span>
                <span className="chat-option-text">{item.displayText}</span>
              </motion.button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="wizard-nav">
          <button
            className="wizard-nav-btn"
            onClick={onWizardPrev}
            disabled={isFirstStep}
          >
            <LeftOutlined /> Geri
          </button>
          {isLastStep ? (
            <motion.button
              className="chat-compare-btn"
              onClick={onWizardSubmit}
              disabled={totalSelections === 0}
              whileHover={totalSelections > 0 ? { scale: 1.02 } : {}}
              whileTap={totalSelections > 0 ? { scale: 0.98 } : {}}
            >
              <CheckOutlined /> Paketi Oner
            </motion.button>
          ) : (
            <button
              className="wizard-nav-btn wizard-nav-btn-next"
              onClick={onWizardNext}
            >
              Ileri <RightOutlined />
            </button>
          )}
        </div>

        {/* Ana Menuye Don — wizard disinda her zaman gorunur */}
        {mainMenuOption && (
          <motion.button
            className="chat-option-btn main-menu"
            onClick={() => onSelect(mainMenuOption)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            style={{ marginTop: 4 }}
          >
            <span className="chat-option-emoji">{mainMenuOption.emoji}</span>
            <span className="chat-option-text">{mainMenuOption.displayText}</span>
          </motion.button>
        )}
      </div>
    );
  }

  // Default render (flat checklist, compare, normal)
  return (
    <div ref={listRef} className={`chat-options ${isInline ? 'chat-options--inline' : ''}`}>
      <div className="chat-options-list">
        {options.map((option, index) => {
          const selected = isSelectionMode && isSelected(option);
          const disabled = isDisabled(option);
          const isMainMenu = option.optionType === 'MAIN_MENU';

          return (
            <motion.button
              key={option.optionType}
              className={`chat-option-btn${selected ? ' selected' : ''}${disabled ? ' compare-disabled' : ''}${isMainMenu && isSelectionMode ? ' main-menu' : ''}`}
              onClick={() => !disabled && handleClick(option)}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: index * 0.05 }}
              whileHover={!disabled ? { scale: 1.01 } : {}}
              whileTap={!disabled ? { scale: 0.98 } : {}}
              disabled={disabled}
            >
              {isSelectionMode && !isMainMenu && (
                <span className={`chat-option-check${selected ? ' checked' : ''}`}>
                  {selected && <CheckOutlined />}
                </span>
              )}
              <span className="chat-option-emoji">{option.emoji}</span>
              <span className="chat-option-text">{option.displayText}</span>
            </motion.button>
          );
        })}
      </div>

      {compareMode && (
        <motion.button
          className="chat-compare-btn"
          onClick={onCompare}
          disabled={selectedCards?.length !== 2}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          whileHover={selectedCards?.length === 2 ? { scale: 1.02 } : {}}
          whileTap={selectedCards?.length === 2 ? { scale: 0.98 } : {}}
        >
          <SwapOutlined /> Karsilastir
        </motion.button>
      )}

      {checklistMode && (
        <motion.button
          className="chat-compare-btn"
          onClick={onRecommend}
          disabled={selectedCriteria?.length === 0}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          whileHover={selectedCriteria?.length > 0 ? { scale: 1.02 } : {}}
          whileTap={selectedCriteria?.length > 0 ? { scale: 0.98 } : {}}
        >
          <CheckOutlined /> Paketi Oner
        </motion.button>
      )}
    </div>
  );
};

export default ChatOptions;
